/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    SimulationSettings,
    CustomerProfile,
    IndustrySpecificData,
    TelecomSpecifics,
    RetailSpecifics,
    BaseCustomerProfile
} from '../types';

export function formatTime(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function escapeHtml(unsafe: string): string {
    if (typeof unsafe !== 'string') return '';
    return unsafe.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

export function decodeHtmlEntities(html: string): string {
    if (typeof html !== 'string') return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
}

export function decodeObjectStrings(obj: any): any {
    if (typeof obj === 'string') {
        return decodeHtmlEntities(obj);
    } else if (Array.isArray(obj)) {
        return obj.map(item => decodeObjectStrings(item));
    } else if (typeof obj === 'object' && obj !== null) {
        const newObj: { [key: string]: any } = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                newObj[key] = decodeObjectStrings(obj[key]);
            }
        }
        return newObj;
    }
    return obj;
}

export function sanitizeJsonString(jsonString: string): string {
    // eslint-disable-next-line no-control-regex
    return jsonString.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');
}

export function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export function subtractDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
}

export function encode(bytes: Uint8Array): string {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export function decode(base64: string): Uint8Array {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
}

export async function decodeAudioData(data: Uint8Array, ctx: AudioContext, sampleRate: number, numChannels: number): Promise<AudioBuffer> {
    const dataInt16 = new Int16Array(data.buffer);
    const frameCount = dataInt16.length / numChannels;
    const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
    for (let channel = 0; channel < numChannels; channel++) {
        const channelData = buffer.getChannelData(channel);
        for (let i = 0; i < frameCount; i++) {
            channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
        }
    }
    return buffer;
}

export function createBlob(data: Float32Array) {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) {
        int16[i] = Math.max(-1, Math.min(1, data[i])) * 0x7FFF;
    }
    return {
        data: encode(new Uint8Array(int16.buffer)),
        mimeType: 'audio/pcm;rate=16000',
    };
}

export function getRandomElement<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

export function generateRandomAddress(): string {
    const streetNumbers = ["123", "45", "7890", "221B", "1000"];
    const streetNames = ["Oak St", "Pine Ave", "Maple Dr", "Willow Ln", "Elm Rd", "Main St", "Sunshine Bvd"];
    const cities = ["Springfield", "Shelbyville", "Anytown", "Fairview", "Riverdale", "Pleasantville", "Metropolis"];
    const states = ["IL", "CA", "NY", "TX", "FL", "WA", "CO"];
    const zipCodes = ["62704", "90210", "10001", "75001", "33101", "98101", "80202"];
    return `${getRandomElement(streetNumbers)} ${getRandomElement(streetNames)}, ${getRandomElement(cities)}, ${getRandomElement(states)} ${getRandomElement(zipCodes)}`;
}

export function generateAccountHistorySummary(): string {
    const histories = ["Customer since 2018. Generally positive payment history. One late payment noted in 2022.", "New customer, account opened 3 months ago. No significant activity yet.", "Long-term customer (10+ years). Multiple products/services utilized. VIP status.", "Customer for 2 years. Recently reported an issue which was resolved successfully.", "Account in good standing. No recent issues reported.", "Customer since 2020. Frequent user of online portal. One previous billing inquiry.", "Account was briefly suspended due to non-payment last year but has been current since.", "Relatively new customer (6 months). Two inquiries resolved previously.", "Loyal customer with a history of positive interactions."];
    return getRandomElement(histories);
}

const maleFirstNames = ["James", "John", "Robert", "Michael", "William", "David", "Richard", "Joseph", "Thomas", "Charles", "Christopher", "Daniel", "Matthew", "Anthony", "Mark", "Donald", "Steven", "Paul", "Andrew", "Joshua", "Kenneth", "Kevin", "Brian", "George", "Edward", "Ronald", "Timothy", "Jason", "Jeffrey", "Ryan", "Jacob", "Gary", "Nicholas", "Eric", "Jonathan", "Stephen", "Larry", "Justin", "Scott", "Brandon", "Benjamin", "Samuel", "Gregory", "Frank", "Alexander", "Patrick", "Raymond", "Jack", "Dennis", "Jerry"];
const femaleFirstNames = ["Mary", "Patricia", "Jennifer", "Linda", "Elizabeth", "Barbara", "Susan", "Jessica", "Sarah", "Karen", "Nancy", "Lisa", "Betty", "Margaret", "Sandra", "Ashley", "Kimberly", "Emily", "Donna", "Michelle", "Dorothy", "Carol", "Amanda", "Melissa", "Deborah", "Stephanie", "Rebecca", "Sharon", "Laura", "Cynthia", "Kathleen", "Amy", "Shirley", "Angela", "Helen", "Anna", "Brenda", "Pamela", "Nicole", "Emma", "Samantha", "Katherine", "Christine", "Debra", "Rachel", "Catherine", "Janet", "Ruth", "Maria", "Priya"];
const lastNames = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson", "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker", "Young", "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill", "Flores", "Green", "Adams", "Nelson", "Baker", "Hall", "Rivera", "Campbell", "Mitchell", "Carter", "Roberts"];

export function generateNameForGender(gender: 'male' | 'female'): string {
    if (gender === 'male') {
        return `${getRandomElement(maleFirstNames)} ${getRandomElement(lastNames)}`;
    } else {
        return `${getRandomElement(femaleFirstNames)} ${getRandomElement(lastNames)}`;
    }
}

const supportBrandNames = ["Northstar", "Bluecrest", "Brightway", "Evergreen", "Summit", "Clearview", "Silverline", "Horizon", "Pinnacle", "Meridian", "Crestwood", "Lakeshore"];
const supportDepartmentSuffixes = ["Customer Care Team", "Customer Support Team", "Customer Service Department", "Client Support Desk", "Customer Relations Team"];

/**
 * Generates a dummy customer support department name used as the recipient of the
 * customer's emails. If a company name is known (e.g., from the Knowledge Base),
 * it is used as the brand; otherwise a fictional brand is chosen.
 */
export function generateSupportDepartmentName(companyName?: string): string {
    const brand = companyName && companyName.trim().length > 1 ? companyName.trim() : getRandomElement(supportBrandNames);
    return `${brand} ${getRandomElement(supportDepartmentSuffixes)}`;
}

export function generateCustomerProfileData(settings: Pick<SimulationSettings, 'industry' | 'selectedGender'>): CustomerProfile {
    let name = "Customer";
    let gender: 'male' | 'female';

    if (settings.selectedGender === 'Male') {
        gender = 'male';
    } else if (settings.selectedGender === 'Female') {
        gender = 'female';
    } else {
        gender = Math.random() > 0.5 ? 'male' : 'female';
    }

    // Generate random name based on gender
    name = generateNameForGender(gender);

    const accountId = `CUST-${Math.floor(Math.random() * 90000) + 10000}`;
    const email = `${name.toLowerCase().replace(' ', '.').replace(/[^a-z0-9.]/gi, '')}@example-domain.com`;
    const phone = `555-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`;
    const address = generateRandomAddress();
    const accountHistorySummary = generateAccountHistorySummary();
    const today = new Date();
    let industrySpecifics: IndustrySpecificData;

    switch (settings.industry) {
        case "Telecom":
            industrySpecifics = {
                planName: getRandomElement(["Basic Connect", "ConnectMax", "UltraFiber 1G", "FamilyTalk Plus"]),
                monthlyBill: `$${(Math.random() * 70 + 30).toFixed(2)}`,
                contractEndDate: addDays(today, Math.floor(Math.random() * 200) + 60).toLocaleDateString(),
                recentActivity: getRandomElement(["International roaming charges of $15.00 on last bill.", "Data overage fee of $10.00.", "Late payment fee applied.", "No unusual activity."]),
                equipment: getRandomElement(["Smartphone Model T5", "Router Model X200", "Smart Hub G3", "Cable Modem CM100"])
            } as TelecomSpecifics;
            break;
        case "Retail":
            industrySpecifics = {
                lastOrderNumber: `ORD-${Math.floor(Math.random() * 80000) + 10000}`,
                lastOrderDate: subtractDays(today, Math.floor(Math.random() * 20) + 2).toLocaleDateString(),
                itemsInLastOrder: getRandomElement(["Premium Coffee Maker", "Wireless Earbuds", "Yoga Mat", "Smart Home Hub", "Designer Jeans", "Running Shoes"]),
                loyaltyTier: getRandomElement(["Bronze", "Silver", "Gold", "Platinum"]) + " Member",
                loyaltyPoints: Math.floor(Math.random() * 1500) + 100,
                shippingAddressUsedForOrder: generateRandomAddress()
            } as RetailSpecifics;
            break;
        default:
            industrySpecifics = {} as any;
    }

    return {
        name,
        gender,
        accountId,
        email,
        phone,
        address,
        accountHistorySummary,
        industrySpecifics,
        status: 'Active',
        history: accountHistorySummary,
        keyPoints: [],
        sentiment: 'Neutral'
    };
}

export function parseProfileFromKB(kbText: string, _industry: string): Partial<BaseCustomerProfile> & { industrySpecifics?: Partial<IndustrySpecificData>, personality?: string, issue?: string, background?: string, companyName?: string } {
    if (!kbText || kbText.trim().length === 0) {
        return {};
    }

    const result: any = {};

    // 1. Identify the primary search area (Customer Profile section)
    const sectionMatch = kbText.match(/---\s*CUSTOMER PROFILE START\s*---([\s\S]*?)---\s*CUSTOMER PROFILE END\s*---/i);
    const profileText = sectionMatch ? sectionMatch[1].trim() : kbText;
    const lowerProfileText = profileText.toLowerCase();

    // 2. Extract Name - LOOK FOR "Name:" tag as top priority
    // Use [ \t] instead of \s to prevent capturing across newlines
    const explicitNameMatch = profileText.match(/\bName:?[ \t]*([A-Z][a-z]+(?:[ \t]+[A-Z][a-z]+){0,3})/i);
    if (explicitNameMatch) {
        result.name = explicitNameMatch[1].trim();
    } else {
        // Fallback name patterns if "Name:" tag not found
        const namePatterns = [
            /(?:customer|client|patient|user|caller)?[ \t]?name:?[ \t]*([A-Z][a-z]+(?:[ \t]+[A-Z][a-z]+){0,3})/i,
            /(?:my name is|i am|i'm|this is)[ \t]+([A-Z][a-z]+(?:[ \t]+[A-Z][a-z]+){0,3})/i,
            /^([A-Z][a-z]+(?:[ \t]+[A-Z][a-z]+){0,3})[ \t]*(?:here|speaking|calling)/im,
            /member:[ \t]*([A-Z][a-z]+(?:[ \t]+[A-Z][a-z]+){0,3})/i
        ];

        for (const pattern of namePatterns) {
            const match = profileText.match(pattern);
            if (match && match[1] && match[1].trim().length > 1) {
                result.name = match[1].trim();
                break;
            }
        }
    }

    // 3. Fallback: If still no name, check the very first line of the profile text
    if (!result.name) {
        const lines = profileText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length > 0) {
            const firstLine = lines[0];
            if (/^[A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2}$/.test(firstLine)) {
                result.name = firstLine;
            }
        }
    }

    // 4. Extract gender from the profile area
    if (/\b(he|him|his|himself|male|man|gentleman|boy)\b/i.test(profileText)) {
        result.gender = 'male';
    }
    if (/\b(she|her|hers|herself|female|woman|lady|girl)\b/i.test(profileText)) {
        if (!result.gender || (lowerProfileText.includes('she is') || lowerProfileText.includes('her name is'))) {
            result.gender = 'female';
        }
    }

    // 5. Extract other fields from the profile area
    const addressMatch = profileText.match(/(?:address|location|residence):?\s*([\d\w\s,.-]+(?:\n[\d\w\s,.-]+)*)/i);
    if (addressMatch && addressMatch[1]) {
        const addr = addressMatch[1].split('\n')[0].trim();
        if (addr.length > 5) result.address = addr;
    }

    const emailMatch = profileText.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
    if (emailMatch) {
        result.email = emailMatch[1];
    }

    const phoneMatch = profileText.match(/(?:phone|tel|call|mobile|contact)(?:\s*number)?:?\s*([\d\s\-\(\).+Ext]+)/i);
    if (phoneMatch && phoneMatch[1]) {
        result.phone = phoneMatch[1].trim();
    }

    const accountMatch = profileText.match(/(?:account|customer|member|policy|order)(?:\s*(?:number|id|#))?:?\s*([A-Z0-9\-]+)/i);
    // Require at least one digit so labels like "Account Name" aren't mistaken for an account ID
    if (accountMatch && accountMatch[1] && accountMatch[1].length > 3 && /\d/.test(accountMatch[1])) {
        result.accountId = accountMatch[1].trim();
    }

    // Company / business the customer is dealing with (searched across the whole KB)
    const companyMatch = kbText.match(/\b(?:company|business|brand|merchant|provider|store|shop|bank)(?:[ \t]+name)?[ \t]*:[ \t]*([^\n\r]{2,60})/i);
    if (companyMatch && companyMatch[1]) {
        const company = companyMatch[1].trim().replace(/[.,;]+$/, '');
        if (company.length > 1) result.companyName = company;
    }

    // Store the entire KB text as additional context
    result.personality = kbText;
    result.issue = kbText;
    result.background = kbText;

    console.log("Parsed KB Profile:", result);
    return result;
}

const MAX_RETRIES = 1;
const INITIAL_BACKOFF_MS = 2500;

export function getUserFriendlyErrorMessage(error: any): string {
    const errorMessage = String(error).toLowerCase();

    if (errorMessage.includes('unauthenticated')) {
        return "Authentication error. Your session may have expired. Please try logging out and back in.";
    }
    if (errorMessage.includes('permission-denied')) {
        return "Permission denied. You do not have access to this resource.";
    }
    if (errorMessage.includes('deadline-exceeded')) {
        return "The request to the backend service timed out. This can happen during periods of high demand. Please try again in a moment.";
    }

    if (errorMessage.includes('429') || errorMessage.includes('resource_exhausted')) {
        return "The service is currently busy due to high demand (Rate Limit Exceeded). Please wait a minute before trying again.";
    }
    if (errorMessage.includes('api_key_invalid') || errorMessage.includes('permission_denied')) {
        return "There is an issue with the API Key configuration on the backend. Please contact the administrator.";
    }
    if (errorMessage.includes('failed to fetch') || errorMessage.includes('network')) {
        return "A network error occurred while communicating with the backend service. Please check your internet connection and try again.";
    }
    if (error instanceof Error) {
        return `An unexpected error occurred: ${error.message}`;
    }
    return `An unexpected error occurred: ${String(error)}`;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function callApiWithRetry<T>(
    apiCallFn: () => Promise<T>,
    onRetryAttempt?: (attempt: number, error: any) => void
): Promise<T> {
    let attempts = 0;
    while (attempts < MAX_RETRIES) {
        try {
            return await apiCallFn();
        } catch (error: any) {
            attempts++;
            if (onRetryAttempt) {
                onRetryAttempt(attempts, error);
            }
            console.warn(`API call failed (attempt ${attempts}/${MAX_RETRIES}):`, error);

            const errorString = String(error).toLowerCase();

            const isTimeoutError = errorString.includes('deadline-exceeded');
            const isRateLimitError = errorString.includes('429') || errorString.includes('resource_exhausted');
            const isServerError = errorString.includes('500') || errorString.includes('503') || errorString.includes('unavailable') || errorString.includes('internal');
            const isNetworkError = errorString.includes('failed to fetch') || errorString.includes('network');

            if (errorString.includes('unauthenticated') || errorString.includes('permission-denied')) {
                console.error("Authentication/Permission error. Halting retries.");
                throw error;
            }

            if (!isTimeoutError && !isRateLimitError && !isServerError && !isNetworkError) {
                console.error("API call failed with a non-retriable error. It will not be retried.", error);
                throw error;
            }

            if (attempts >= MAX_RETRIES) {
                console.error("API call failed after maximum retries.", error);
                throw error;
            }

            const jitter = Math.random() * 1000;
            const delay = INITIAL_BACKOFF_MS * Math.pow(2, attempts - 1) + jitter;
            console.log(`Retrying in ${Math.round(delay)}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    throw new Error("API call failed after retries - unexpected state.");
}

export async function parseGeminiJsonResponse(jsonString: string): Promise<any | null> {
    let cleanJsonString = jsonString.trim();
    const fenceRegex = /^```(?:json)?\s*\n?(.*?)\n?\s*```$/s;
    const match = cleanJsonString.match(fenceRegex);
    if (match && match[1]) {
        cleanJsonString = match[1].trim();
    }

    cleanJsonString = sanitizeJsonString(cleanJsonString);
    console.log("Attempting to parse JSON:", cleanJsonString);

    try {
        const parsedData = JSON.parse(cleanJsonString);
        return decodeObjectStrings(parsedData);
    } catch (e: any) {
        console.error("Failed to parse JSON response details:", e);
        return null;
    }
}
